import React, {useEffect, useState} from "react";
import PageHeaderLayout from "../../layout/pageHeaderLayout";
import {
    Button,
    Card,
    Checkbox,
    Col,
    Form,
    Input,
    Row,
    Space,
    Table,
    Select as AntSelect,
    message,
    InputNumber
} from "antd";
import ProfileTitle from "../../components/ProfileTitle";
import Upload from "../../components/Upload";
import dayjs from "dayjs";
import AreaSelect from "../../components/AreaSelect";
import {useParams} from "react-router";
import {useHistory} from "react-router-dom";
import {useForm, useWatch} from "antd/lib/form/Form";
import {useRequest} from "ahooks";
import * as api from "../../apis/dealer";
import * as utils from "../../utils/utils";
import * as minioUtil from "../../utils/minio";
import DatePicker from "../../components/DatePicker";
import BaseDataSelect from "../../components/BaseDataSelect";
import Select from "../../components/Select";
import DictSelect from "../../components/DictSelect";
import {DealerVersionStatusMapping} from "../../utils/constants";
import {dealerStatusOptions} from "../../utils/searchOptions";
import withAuth from "../../utils/withAuth";
import useAuth from "../../utils/useAuth";


enum DetailMode {
    Add,
    View,
    Edit,
    Alter,
    Version,
    VersionEdit
}

const StatusButton = withAuth('status', Button)
const ApproveButton = withAuth('approve', Button)
const ERPButton = withAuth('erp', Button)
const CompleteButton = withAuth('complete', Button)

function DealerDetail() {

    const {id} = useParams<{ id: string }>()
    const history = useHistory()
    const [mode, setMode] = useState<DetailMode>(history.location.pathname.indexOf('version') !== -1 ? DetailMode.Version : (id ? DetailMode.View : DetailMode.Add))
    const [form] = useForm()
    const [dealer, setDealer] = useState<any>({})
    const [licenses, setLicenses] = useState<any[]>([])
    const [versions, setVersions] = useState<any[]>([])

    const invoiceType = useWatch('invoiceType', form)

    const viewTemporaryPeriod = useAuth().indexOf('fieldTemporaryPeriodView') > -1
    const editTemporaryPeriod = useAuth().indexOf('fieldTemporaryPeriodEdit') > -1
    const viewTemporaryCreditLimit = useAuth().indexOf('fieldTemporaryCreditLimitView') > -1
    const editTemporaryCreditLimit = useAuth().indexOf('fieldTemporaryCreditLimitEdit') > -1

    useEffect(() => {
        if (mode === DetailMode.Add) {
            api.getLicenses().then(response => setLicenses(response.data.data))
        } else if (mode === DetailMode.View) {
            loadData(id)
        } else if (mode === DetailMode.Version) {
            loadVersionData(id)
        }
    }, [])

    function loadData(orderId: string) {
        setMode(DetailMode.View)
        api.getDealer(orderId).then(response => {
            form.setFieldsValue(response.data.data)
            setDealer(response.data.data)
            setLicenses(response.data.data.licenses)
        })
        api.getDealerVersions(orderId).then(response => {
            setVersions(response.data.data)
        })
    }

    function loadVersionData(orderId: string) {
        setMode(DetailMode.Version)
        api.getDealerVersion(orderId).then(response => {
            form.setFieldsValue(response.data.data)
            setDealer(response.data.data)
            setLicenses(response.data.data.licenses)
        })
    }

    const {run: submit, loading: submitting} = useRequest(onSubmit, {
        manual: true
    })

    const {run: onOASubmit, loading: oaSubmitting} = useRequest(api.submitDealerToOA, {
        manual: true,
        onSuccess: () => {
            loadVersionData(id)
            message.success("OA送审成功")
        }
    })

    const {run: onERPSubmit, loading: erpSubmitting} = useRequest(api.submitDealerToERP, {
        manual: true,
        onSuccess: () => {
            loadVersionData(id)
            message.success("ERP同步成功")
        }
    })

    const {run: onStatusToggle, loading: onStatusToggling} = useRequest(api.toggleDealerStatus, {
        manual: true,
        onSuccess: () => {
            loadData(id)
        }
    })

    const {run: onComplete, loading: onCompleting} = useRequest(api.completeDealer, {
        manual: true,
        onSuccess: () => {
            loadVersionData(id)
            message.success("终审通过成功")
        }
    })

    const {run: onDelete, loading: onDeleting} = useRequest(api.deleteDealerVersion, {
        manual: true,
        onSuccess: () => {
            history.goBack()
            message.success("删除成功")
        }
    })

    const checkMode = (...modes: DetailMode[]) => {
        if (mode === DetailMode.Add) return false
        else if (mode === DetailMode.View) return true
        else return modes.indexOf(mode) === -1
    }

    const versionColumns = [
        {
            title: '变更日期',
            dataIndex: 'createTime',
            render: (value: any) => dayjs(value).format('ll')
        },
        {
            title: '变更人',
            dataIndex: 'createName',
        },
        {
            title: '变更编码',
            dataIndex: 'no',
        },
        {
            title: '变更说明',
            dataIndex: 'description',
        },
        {
            title: '审核编号',
            dataIndex: 'flowId',
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (value: any) => DealerVersionStatusMapping[value]
        },
        {
            title: '操作',
            dataIndex: 'operations',
            render: (_: any, record: any) => (
                <span>
                    <a onClick={() => {
                        history.push('/customer/dealer/version/' + record.id)
                    }}>详情</a>
                </span>
            )
        }
    ]

    async function onSubmit() {
        const data = await form.validateFields()

        if (data.qualification2) {
            if (!data.licenses.find((item: any) => item.id === 2 || item.licenseId === 2)?.path) {
                message.info("请上传《备案凭证(2类)》")
                return
            }
        }
        if (data.licenses.find((item: any) => item.id === 2 || item.licenseId === 2)?.path) {
            if (!data.qualification2) {
                message.info("请勾选二类资质")
                return
            }
        }

        if (data.qualification3) {
            if (!data.licenses.find((item: any) => item.id === 3 || item.licenseId === 3)?.path) {
                message.info("请上传《备案凭证(3类)》")
                return
            }
            if (!data.licenses.find((item: any) => item.id === 3 || item.licenseId === 3)?.expire) {
                message.info("请上传《备案凭证(3类)》有效期")
                return
            }
        }
        if (data.licenses.find((item: any) => item.id === 3 || item.licenseId === 3)?.path) {
            if (!data.qualification3) {
                message.info("请勾选三类资质")
                return
            }
        }

        if (data.shipping.filter((item: any) => item.default).length != 1) {
            message.info("必须有一个收货地址勾选默认地址")
            return
        }

        for (const item of data.licenses) {
            if (item.path?.originFileObj) {
                const key = utils.generateFileName(`dealer/license/`, item.path.name);
                item.path = await minioUtil.upload(item.path.originFileObj, key);
            }
        }
        if (mode === DetailMode.Add) {
            return api.addDealer(data).then(response => {
                history.replace('/customer/dealer/version/' + response.data.data)
                // loadData(response.data.data)
            })
        } else if (mode === DetailMode.Edit) {
            return api.editDealer(data).then(() => {
                loadData(id)
            })
        } else if (mode === DetailMode.Alter) {
            return api.alterDealer(data).then(response => {
                history.push('/customer/dealer/version/' + response.data.data)
            })
        } else if (mode === DetailMode.VersionEdit) {
            return api.editDealerVersion({...data, id: id}).then(() => {
                loadVersionData(id)
            })
        }
    }

    return (
        <PageHeaderLayout withFooterBar={mode !== DetailMode.View && mode !== DetailMode.Version}
                          onOk={submit}
                          btnLoading={submitting}
                          onCancel={() => {
                              if (mode === DetailMode.Add) {
                                  history.goBack()
                              } else if (mode === DetailMode.VersionEdit) {
                                  setMode(DetailMode.Version)
                              } else {
                                  setMode(DetailMode.View)
                              }
                          }}>
            <Space style={{alignItems: 'stretch', width: '100%'}} direction={"vertical"}>
                <Card title={'基本信息'} extra={(
                    <Space>
                        {mode === DetailMode.View && (
                            <>
                                {!versions.some(i => i.status !== 2 && i.status !== 3) && (
                                    <>
                                        <Button onClick={() => setMode(DetailMode.Alter)}>变更</Button>
                                        <Button onClick={() => setMode(DetailMode.Edit)}>修改</Button>
                                    </>
                                )}
                                {dealer.status === 3 && (
                                    <StatusButton onClick={() => onStatusToggle(dealer.id, 2)}
                                                  loading={onStatusToggling}>启用</StatusButton>
                                )}
                                {dealer.status === 2 && (
                                    <StatusButton onClick={() => onStatusToggle(dealer.id, 3)}
                                                  loading={onStatusToggling}>停用</StatusButton>
                                )}
                            </>
                        )}
                        {mode === DetailMode.Version && dealer.status !== 2 && (
                            <CompleteButton onClick={() => onComplete(dealer.id)}
                                            loading={onCompleting}>终审通过</CompleteButton>
                        )}
                        {mode === DetailMode.Version && dealer.status === 0 && (
                            <>
                                <ApproveButton onClick={() => onOASubmit(dealer.id)}
                                               loading={oaSubmitting}>OA送审</ApproveButton>
                                <ERPButton onClick={() => onERPSubmit(dealer.id)}
                                           loading={erpSubmitting}>ERP同步</ERPButton>
                                <Button onClick={() => setMode(DetailMode.VersionEdit)}>修改</Button>
                                <Button onClick={() => onDelete(id)} loading={onDeleting}>删除</Button>
                            </>
                        )}
                        {mode === DetailMode.Version && dealer.status === 2 && (
                            <ERPButton onClick={() => onERPSubmit(dealer.id)}
                                       loading={erpSubmitting}>ERP同步</ERPButton>
                        )}
                    </Space>
                )}>
                    <Form labelCol={{span: 6}} form={form}>
                        <Form.Item name={'id'} noStyle>
                            <Input style={{display: 'none'}}/>
                        </Form.Item>
                        {[DetailMode.Alter, DetailMode.VersionEdit].indexOf(mode) !== -1 && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item labelCol={{span: 2}} name={'description'} label={'变更说明'}
                                               rules={[{required: true}]}>
                                        <Input.TextArea disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'no'} label={'客户编码'}>
                                    <Input disabled/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'name'} label={'客户名称'} rules={[{required: true}]}
                                           normalize={value => value.trim()}>
                                    <Input onChange={event => form.setFieldValue('invoiceName', event.target.value)}
                                           disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'nameShort'} label={'客户简称'}>
                                    <Input disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'dealerGroup'} label={'客户组'} rules={[{required: true}]}>
                                    <BaseDataSelect name={'dealerGroups'} appendNull={false}
                                                    disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'group'} label={'集团'}>
                                    <BaseDataSelect name={'groups'}
                                                    disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'area'} label={'销售地区'} rules={[{required: true}]}>
                                    <BaseDataSelect name={'saleAreas'} appendNull={false}
                                                    disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'country'} label={'国家'} rules={[{required: true}]}>
                                    <BaseDataSelect name={'countries'} appendNull={false}
                                                    defaultSelectFirst
                                                    disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'currency'} label={'币种'} rules={[{required: true}]}
                                           initialValue={'CNY'}>
                                    <BaseDataSelect name={'currencies'} appendNull={false}
                                                    disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'property'} label={'客户性质'} rules={[{required: true}]}>
                                    <BaseDataSelect name={'dealerNatures'} appendNull={false}
                                                    disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'zipCode'} label={'客户邮编'}>
                                    <Input
                                        disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'businessUser'} label={'商务人员'} rules={[{required: true}]}>
                                    <Select url={'/position/dict?type=1'} getData={data => data.data.map((i: any) => ({
                                        ...i,
                                        name: `${i.name}(${i.user})`
                                    }))} textField={'name'} valueField={'id'}
                                            disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'salesUser'} label={'销售人员'} rules={[{required: true}]}>
                                    <Select url={'/position/dict?type=0'} getData={data => data.data.map((i: any) => ({
                                        ...i,
                                        name: `${i.name}(${i.user})`
                                    }))} textField={'name'} valueField={'id'}
                                            disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'legalRepresentative'} label={'法人代表'}>
                                    <Input
                                        disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'registeredCapital'} label={'注册资金'}>
                                    <Input disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'registeredDate'} label={'成立日期'}>
                                    <DatePicker style={{width: '100%'}} allowClear
                                                disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'status'} label={'状态'}>
                                    <AntSelect disabled>
                                        {dealerStatusOptions.map(i => (
                                            <AntSelect.Option key={i.value} value={i.value}>{i.text}</AntSelect.Option>
                                        ))}
                                    </AntSelect>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'manager'} label={'总经理姓名'} rules={[{required: true}]}>
                                    <Input
                                        disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'managerPhone'} label={'总经理电话'} rules={[{required: true}]}>
                                    <Input
                                        disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'paymentMethod'} label={'付款方式'} initialValue={'电汇'}>
                                    <DictSelect dict={'PaymentMethod'} appendNull={false}
                                                disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'salesUser2'} label={'销售人员2'}>
                                    <Select url={'/position/dict?type=0'} getData={data => data.data.map((i: any) => ({
                                        ...i,
                                        name: `${i.name}(${i.user})`
                                    }))} textField={'name'} valueField={'id'}
                                            disabled={checkMode(DetailMode.Edit, DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name={'creditLimit'} label={'信用额度'}>
                                    <InputNumber min={0} precision={2} addonAfter={'万元'}
                                                 disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}/>
                                </Form.Item>
                            </Col>
                            <Col span={viewTemporaryCreditLimit ? 8 : 0}
                                 style={viewTemporaryCreditLimit ? {} : {display: 'none'}}>
                                <Form.Item name={'temporaryCreditLimit'} label={'临时信用额度'}>
                                    <InputNumber min={0} precision={2} addonAfter={'万元'}
                                                 disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit) || !editTemporaryCreditLimit}/>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name={'period'} label={'账期'}>
                                    <Select url={'/dict/?typeName=DealerPeriod'}
                                            getData={data => data.data}
                                            textField={'value'}
                                            valueField={'value'}
                                            suffixIcon={'天'}
                                            disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={viewTemporaryPeriod ? 8 : 0}
                                 style={viewTemporaryPeriod ? {} : {display: 'none'}}>
                                <Form.Item name={'temporaryPeriod'} label={'临时账期'}>
                                    <Select url={'/dict/?typeName=DealerPeriod'}
                                            allowClear
                                            getData={data => data.data}
                                            textField={'value'}
                                            valueField={'value'}
                                            suffixIcon={'天'}
                                            disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit) || !editTemporaryPeriod}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={4}>
                                <Form.Item name={'includeTax'} initialValue={true}
                                           valuePropName={"checked"} wrapperCol={{offset: 6}}>
                                    <Checkbox disabled={true}>是否含税</Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name={'specialBilling'} initialValue={false}
                                           valuePropName={"checked"}
                                           wrapperCol={{offset: 6}}
                                           tooltip={'标记特殊开票，财务开票时进行过滤'}>
                                    <Checkbox
                                        disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}>特殊开票</Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name={'autoWriteOff'} wrapperCol={{offset: 6}} initialValue={false}
                                           valuePropName={"checked"}>
                                    <Checkbox
                                        disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}>付款单自动核销</Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name={'qualification2'} wrapperCol={{offset: 6}} initialValue={false}
                                           valuePropName={"checked"}>
                                    <Checkbox
                                        disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}>2类资质</Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name={'qualification3'} initialValue={false} valuePropName={"checked"}>
                                    <Checkbox
                                        disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}>3类资质/其他</Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={8}>
                                <Form.Item name={'reason'} label={'申请理由'}>
                                    <Input.TextArea disabled={checkMode()}/>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                    <div>
                        <ProfileTitle>收货地址</ProfileTitle>
                        <Form disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)} form={form}>
                            <Row gutter={10} style={{marginBottom: 10}}>
                                <Col span={4} offset={2}>收货人</Col>
                                <Col span={4}>手机</Col>
                                <Col span={3}>省</Col>
                                <Col span={3}>市</Col>
                                <Col span={4}>详细地址</Col>
                                <Col span={4}>默认地址</Col>
                            </Row>
                            <Form.List name={'shipping'} initialValue={[{}]}>
                                {(fields, {add, remove}) => (
                                    fields.map(field => (
                                        <Row gutter={10} key={field.name}>
                                            <Col span={2}>
                                                <Space>
                                                    <Button onClick={() => add()}>+</Button>
                                                    <Button
                                                        onClick={() => fields.length > 1 && remove(field.name)}>-</Button>
                                                </Space>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item name={[field.name, 'name']}
                                                           rules={[{required: true, message: '请输入收货人'}]}>
                                                    <Input/>
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item name={[field.name, 'phone']}
                                                           rules={[{required: true, message: '请输入手机'}]}>
                                                    <Input/>
                                                </Form.Item>
                                            </Col>
                                            <Col span={3}>
                                                <Form.Item name={[field.name, 'provinceId']}
                                                           rules={[{required: true, message: '请选择省份'}]}>
                                                    <AreaSelect/>
                                                </Form.Item>
                                            </Col>
                                            <Col span={3}>
                                                <Form.Item dependencies={[['shipping', field.name, 'provinceId']]}
                                                           noStyle>
                                                    {({getFieldValue}) => {
                                                        const province = getFieldValue(['shipping', field.name, 'provinceId'])
                                                        return (
                                                            <Form.Item name={[field.name, 'cityId']}
                                                                       rules={[{
                                                                           required: true,
                                                                           message: '请选择城市'
                                                                       }]}>
                                                                <AreaSelect type={'city'}
                                                                            parent={province}/>
                                                            </Form.Item>
                                                        )
                                                    }}
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item name={[field.name, 'address']}
                                                           rules={[{required: true, message: '请输入详细地址'}]}>
                                                    <Input/>
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item name={[field.name, 'default']}
                                                           valuePropName={'checked'}
                                                           initialValue={field.name === 0}>
                                                    <Checkbox onChange={event => {
                                                        const shipping = form.getFieldValue('shipping')
                                                        form.setFieldValue("shipping", shipping.map((i: any, index: number) => ({
                                                            ...i,
                                                            default: field.name === index ? event.target.checked : false
                                                        })))
                                                    }}/>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))
                                )}
                            </Form.List>
                        </Form>
                    </div>
                    <br/>
                    <div>
                        <ProfileTitle>开票信息</ProfileTitle>
                        <Form labelCol={{span: 6}} disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}
                              form={form}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={'invoiceName'} label={'名称'} rules={[{required: true}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'invoiceType'} label={'发票类型'} rules={[{required: true}]}>
                                        <DictSelect dict={'InvoiceType'} appendNull={false} onChange={(value) => {
                                            if (value.startsWith('special')) {
                                                form.setFieldValue('salesTaxGroup', 'All-专票')
                                            } else {
                                                form.setFieldValue('salesTaxGroup', 'All-普票')
                                            }
                                        }}/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'taxNo'} label={'税号'} rules={[{required: true}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={'bankName'} label={'开户银行'}
                                               rules={[{required: true}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'bankNo'} label={'银行账户'}
                                               rules={[{required: true}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'phone'} label={'电话号码'}
                                               rules={[{required: true}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={'address'} label={'开票地址'}
                                               rules={[{required: true}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'invoiceEmail'} label={'电子邮箱'}
                                               rules={[{required: ['ordinary-E', 'special-E'].indexOf(invoiceType) > -1}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'salesTaxGroup'} label={'销售税组'}
                                               rules={[{required: true}]} initialValue={'All-专票'}>
                                        <BaseDataSelect name={'salesTaxGroups'} appendNull={false}/>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={'invoiceAddress'} label={'发票邮寄地址'}
                                               rules={[{required: ['special', 'ordinary'].indexOf(invoiceType) > -1}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'invoiceRecipient'} label={'发票邮寄联系人'}
                                               rules={[{required: ['special', 'ordinary'].indexOf(invoiceType) > -1}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={'invoicePhone'} label={'发票邮寄联系电话'}
                                               rules={[{required: ['special', 'ordinary'].indexOf(invoiceType) > -1}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                    <br/>
                    <div>
                        <ProfileTitle
                            description={'注：附件最大20M，支持格式：PDF、Word、Excel、Txt、JPG、PNG、BMP、GIF、RAR、ZIP'}>
                            资质文件
                        </ProfileTitle>
                        <Form labelCol={{span: 8}} disabled={checkMode(DetailMode.Alter, DetailMode.VersionEdit)}
                              form={form}>
                            <Form.List name={'licenses'}>
                                {(fields) => (
                                    licenses.map((item: any, index: number) => (
                                            <Row key={item.id} gutter={16}>
                                                <Form.Item name={[index, 'licenseId']} initialValue={item.id}>
                                                    <Input style={{display: 'none'}}/>
                                                </Form.Item>
                                                <Col span={12}>
                                                    <Form.Item name={[index, 'path']} label={item.name}
                                                               rules={[{required: item.required}, {validator: Upload.validator({limit: 20 * 1024 * 1024})}]}>
                                                        <Upload maxCount={1}/>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item label={'有效期'} name={[index, 'expire']}
                                                               rules={[{required: item.requiredExpire}]}>
                                                        <DatePicker.RangePicker/>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        )
                                    )
                                )}
                            </Form.List>
                        </Form>
                    </div>
                    {mode === DetailMode.Add && (
                        <div>
                            <ProfileTitle>登陆账号</ProfileTitle>
                            <Form form={form}>
                                <Row gutter={10} style={{marginBottom: 10}}>
                                    <Col span={8}>角色</Col>
                                    <Col span={8}>姓名</Col>
                                    <Col span={8}>手机</Col>
                                </Row>
                                <Row gutter={10}>
                                    <Col span={8}>管理员账户</Col>
                                    <Col span={8}>
                                        <Form.Item name={'adminName'}
                                                   rules={[{required: true, message: '请输入管理员姓名'}]}>
                                            <Input/>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name={'adminPhone'}
                                                   rules={[{required: true, message: '请输入管理员手机'}]}>
                                            <Input/>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={10}>
                                    <Col span={8}>采购员账号</Col>
                                    <Col span={8}>
                                        <Form.Item name={'salesName'}>
                                            <Input/>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name={'salesPhone'}>
                                            <Input/>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    )}
                </Card>
                {mode === DetailMode.View && (
                    <Card title={'历史版本'}>
                        <Table rowKey={'id'} columns={versionColumns} dataSource={versions}/>
                    </Card>
                )}
            </Space>
        </PageHeaderLayout>
    )
}

export default DealerDetail
